import { useId, useState } from 'react';
import { Link, linkOptions } from '@tanstack/react-router';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileMinusIcon,
  FilePenIcon,
  FilePlusIcon,
  FileSymlinkIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ordinal } from '@/features/concurrency';
import { formatCost, useTaskResults } from '@/features/tasks';
import type { FileChangeEventType } from '@/lib/api/generated/model';
import { filesOfRun, latestAgentRun } from '../whatItProduced';
import type { QueueRow } from '../yourTurn';

/**
 * "What it produced" on a decision (spec 154 US1): the agent's last finished
 * run, read before deciding, without leaving the page.
 *
 * The summary, turns and cost come from the workspace read the page already
 * has. Only the files are read, once the peek is opened, through the same
 * query the drawer's Results tab uses, so either one fills the other's cache.
 */

const KIND: Record<
  FileChangeEventType,
  { word: string; Icon: typeof FilePlusIcon }
> = {
  created: { word: 'new', Icon: FilePlusIcon },
  modified: { word: 'changed', Icon: FilePenIcon },
  deleted: { word: 'removed', Icon: FileMinusIcon },
  moved: { word: 'moved', Icon: FileSymlinkIcon },
};

export function WhatItProduced({
  workspaceId,
  row,
  defaultOpen = false,
}: {
  workspaceId: string;
  row: QueueRow;
  /** The review sheet shows it open. */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className="relative z-10 col-span-2 flex flex-col gap-1.5 pt-1">
      <button
        type="button"
        data-peek-toggle
        aria-expanded={open}
        aria-controls={panelId}
        // Named outright: a visually hidden span gave Chrome "What it
        // produced : Search results", a space the eye never sees.
        aria-label={`What it produced: ${row.title}`}
        onClick={() => {
          setOpen(!open);
        }}
        className="text-primary focus-visible:ring-ring flex items-center gap-1 self-start rounded-sm text-xs font-medium focus-visible:ring-2 focus-visible:outline-none pointer-coarse:min-h-11"
      >
        <Chevron aria-hidden="true" className="size-3.5" />
        What it produced
      </button>
      <div id={panelId} hidden={!open}>
        {open ? <Peek workspaceId={workspaceId} row={row} /> : null}
      </div>
    </div>
  );
}

function Peek({ workspaceId, row }: { workspaceId: string; row: QueueRow }) {
  const run = latestAgentRun(row.task);
  const runLink = row.hasBoard ? runLinkOf(workspaceId, row) : null;
  const results = useTaskResults(workspaceId, row.taskId, {
    enabled: run !== null,
    poll: false,
    live: false,
  });

  const facts = [
    row.attempts > 1 ? `${ordinal(row.attempts)} attempt` : null,
    run?.num_turns != null
      ? `${String(run.num_turns)} ${run.num_turns === 1 ? 'turn' : 'turns'}`
      : null,
    run?.cost != null ? formatCost(run.cost, run.currency) : null,
  ].filter((fact): fact is string => fact !== null);

  return (
    <div className="border-border bg-muted/40 flex flex-col gap-2 rounded-md border px-3 py-2.5 text-xs">
      <p
        className={
          run?.result_summary ? 'text-foreground' : 'text-muted-foreground'
        }
      >
        {run
          ? run.result_summary || 'No summary from the agent.'
          : 'No agent has worked on this task yet.'}
      </p>

      {run ? (
        results.loadFailed ? (
          <p className="text-muted-foreground flex items-center gap-2">
            The files could not be read.
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={results.retry}
            >
              Retry
            </Button>
          </p>
        ) : results.results ? (
          <Files
            results={filesOfRun(results.results, run.id)}
            runLink={runLink}
          />
        ) : (
          <p className="text-muted-foreground">Reading the files…</p>
        )
      ) : null}

      <p className="text-muted-foreground flex flex-wrap items-center gap-x-2">
        {facts.length > 0 ? <span>{facts.join(' · ')}</span> : null}
        {runLink ? (
          <Link
            {...runLink}
            className="text-primary underline-offset-4 hover:underline"
          >
            {run ? 'Open the run' : 'Open the task'}
          </Link>
        ) : null}
      </p>
    </div>
  );
}

/** The task's runs, on its board's drawer. */
function runLinkOf(workspaceId: string, row: QueueRow) {
  return linkOptions({
    to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
    params: { workspaceId, workflowKey: row.workflowKey },
    search: { task: row.taskId, tab: 'executions' as const },
  });
}

function Files({
  results,
  runLink,
}: {
  results: ReturnType<typeof filesOfRun>;
  runLink: ReturnType<typeof runLinkOf> | null;
}) {
  if (results.files.length === 0) {
    return <p className="text-muted-foreground">It changed no files.</p>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {results.files.map((file) => {
        const { word, Icon } = KIND[file.event_type];
        const name = file.path.split('/').pop() ?? file.path;
        return (
          <li key={file.path} className="flex min-w-0 items-center gap-1.5">
            <Icon
              aria-hidden="true"
              className="text-muted-foreground size-3.5 shrink-0"
              strokeWidth={1.75}
            />
            <span
              title={file.path}
              className="text-foreground truncate font-mono"
            >
              {name}
            </span>
            <span className="text-muted-foreground shrink-0">{word}</span>
          </li>
        );
      })}
      {results.more > 0 ? (
        <li>
          {runLink ? (
            <Link
              {...runLink}
              className="text-primary underline-offset-4 hover:underline"
            >
              and {String(results.more)} more
            </Link>
          ) : (
            <span className="text-muted-foreground">
              and {String(results.more)} more
            </span>
          )}
        </li>
      ) : null}
    </ul>
  );
}
